import { Injectable, OnDestroy } from '@angular/core';
import { I18NextPipe } from 'angular-i18next';
import * as _ from 'lodash';
import { LazyLoadEvent } from 'primeng/api';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TableItemState } from '../../component-library/table/table-item-state.enum';
import { TableParameters } from '../../component-library/table/table-parameters';
import { BackendPaginationService } from '../../core/backend-pagination.service';
import { BackendService } from '../../core/backend.service';
import { HbNotification } from '../../notifications/notification';
import { NotificationLife } from '../../notifications/notification-life';
import { NotificationState } from '../../notifications/notification-state';
import { NotificationService } from '../../notifications/notification.service';
import { DirtyFormStateService } from '../../shared/dirty-form-guard/dirty-form-state.service';
import { GenerateGuidService } from '../../shared/generate-guid/generate-guid.service';
import { TableConfigHelperService } from '../../shared/table-helpers/table-config-helper-service';
import { onRowsReorder } from '../../shared/table-helpers/table-helper';
import { HierarchyTemplateAttribute } from '../../viewmodels/admin/hierarchy-templates/hierarchy-template-attribute';
import { HierarchyTemplateGroup } from '../../viewmodels/admin/hierarchy-templates/hierarchy-template-group';
import { HierarchyTemplateTab } from '../../viewmodels/admin/hierarchy-templates/hierarchy-template-tab';
import { RangeDeleteData } from '../../viewmodels/admin/template-attributes-delete-data';
import { HierarchyTemplateAttributesTableConfigService } from './hierarchy-template-attributes-table-config.service';

@Injectable({
  providedIn: 'root',
})
export class HierarchyTemplateAttributesTableService implements OnDestroy {
  public templateId: string;
  private unsubscribe: Subject<boolean> = new Subject();
  public attributesValues$: BehaviorSubject<HierarchyTemplateAttribute[]> = new BehaviorSubject([]);
  public selectedAttributes: HierarchyTemplateAttribute[] = [];
  public allowEditAttributes: boolean = false;
  public attribute: HierarchyTemplateAttribute;
  public hierarchyTemplateGroupId: string;
  public totalRecords: number = 0;
  public selectedHierarchyTemplateTab: HierarchyTemplateTab;
  public selectedHierarchyTemplateGroup: HierarchyTemplateGroup;

  public constructor(
    private backendPaginationService: BackendPaginationService,
    private tableConfigService: HierarchyTemplateAttributesTableConfigService,
    private i18NextPipe: I18NextPipe,
    private backendService: BackendService,
    private notificationService: NotificationService,
    private tableConfigHelperService: TableConfigHelperService,
    private generateGuidService: GenerateGuidService,
    private dirtyFormStateService: DirtyFormStateService
  ) { }

  public setParams(allowEditAttributes: boolean): void {
    this.allowEditAttributes = allowEditAttributes;
  }

  public loadPaginateAttributes(event: LazyLoadEvent): void {
    const tableParams = this.createTableParameters(event);

    this.showTableLoader(true);

    this.paginateAttributes(tableParams)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(result => {
        if (result) {
          this.showTableLoader(false);
          this.assignAttributes(result);
          this.tableConfigService.attributesConfig.enablePagination = true;
        }
      });
  }

  private paginateAttributes(tableParams: TableParameters): Observable<HierarchyTemplateAttribute[]> {
    return this.backendPaginationService.paginateWithParameters<HierarchyTemplateAttribute>(
      HierarchyTemplateAttribute,
      tableParams,
      tableParams.skip,
      tableParams.take,
      false,
    );
  }

  private getCount(tableParameters: TableParameters): Observable<number> {
    return this.backendPaginationService.getCountWithParameters<HierarchyTemplateAttribute>(HierarchyTemplateAttribute, tableParameters, false);
  }

  public createTableParameters(tableState: LazyLoadEvent): TableParameters {
    const params = new TableParameters();

    params.selectedItemId = this.hierarchyTemplateGroupId;
    params.skip = tableState.first;
    params.take = tableState.rows;

    return params;
  }

  public getAttributesTotalRecords(): void {
    const params = new TableParameters();

    params.selectedItemId = this.hierarchyTemplateGroupId;
    this.getCount(params)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(count => {
        if (count) {
          this.tableConfigService.setTablePaginationConfig(count);
          this.totalRecords = count;
        }
      });
  }

  public loadAttributeCollection(): void {
    this.selectAttributes([]);
    this.getAttributesTotalRecords();
    this.loadPaginateAttributes(this.tableConfigService.defaultAttributeLoadEvent);
    this.totalRecords = 0;
  }

  public loadNewTemplateAttributeData(): void {
    this.selectAttributes([]);
    this.attributesValues$.next([]);
    this.tableConfigService.setTablePaginationConfig(0);
    this.updateDeleteAttributesButton();
  }

  public selectAttributes(attributes: HierarchyTemplateAttribute[]): void {
    this.selectedAttributes = attributes;
    this.updateDeleteAttributesButton();
  }

  private updateDeleteAttributesButton(): void {
    this.tableConfigHelperService.updateDeleteAction(
      this.tableConfigService.attributesConfig,
      this.allowEditAttributes,
      this.selectedAttributes.length > 0,
    );
  }

  public get attributeDeleteConfirmationMessage(): string {
    return this.i18NextPipe.transform('HierarchyTemplateAttributes.WillBeDeleted');
  }

  public get deleteConfirmationTitle(): string {
    return this.i18NextPipe.transform('HierarchyTemplateAttributes.Delete');
  }

  public showFailedNotification(description: string): void {
    this.showNotification(description, NotificationState.Failed);
  }

  private showNotification(description: string, state: NotificationState): void {
    const notification = new HbNotification();

    notification.description = description;
    notification.state = state;
    notification.life = NotificationLife.Long;
    this.notificationService.displayToast(notification);
  }

  public deleteHierarchyAttributes(): Observable<boolean> {
    const attributeIdsToDelete = this.selectedAttributes.filter(a => a.state !== TableItemState.Added).map(a => a.hierarchyTemplateAttributeId);

    if (!attributeIdsToDelete || attributeIdsToDelete.length === 0) {
      return of(true);
    }

    const deletionData = new RangeDeleteData(this.templateId, attributeIdsToDelete);

    return this.backendService.deleteRangeObject<HierarchyTemplateAttribute>(HierarchyTemplateAttribute, deletionData);
  }

  public startAttributeLoading = (): void => this.tableConfigService.startLoading(this.tableConfigService.attributesConfig);
  public finishAttributeLoading = (): void => this.tableConfigService.finishLoading(this.tableConfigService.attributesConfig);

  public handleDeletedAttributes(): void {
    this.totalRecords -= this.selectedAttributes.length;
    const attributes = this.updateDeletedAttributesCollection(this.attributesValues$.value, this.selectedAttributes);

    this.assignAttributes(attributes);
    this.selectedAttributes = [];
    this.updateDeleteAttributesButton();
  }

  private updateDeletedAttributesCollection(
    attributes: HierarchyTemplateAttribute[],
    attributesToDelete: HierarchyTemplateAttribute[]
  ): HierarchyTemplateAttribute[] {

    const removedItemsCount = attributesToDelete && attributesToDelete.length;
    if (!removedItemsCount) {
      return attributes;
    }

    const reversedRowNumbers = attributesToDelete.map(a => a.rowNumber).sort((a, b) => a > b ? 1 : -1).reverse();
    return attributes
      .filter(a => !attributesToDelete.find(d => d === a))
      .map(a => {
        for (let i = 0; i < removedItemsCount; i++) {
          if (a.rowNumber > reversedRowNumbers[i]) {
            a.rowNumber -= (removedItemsCount - i);
            return _.cloneDeep(a);
          }
        }
        return a;
      });
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next(true);
    this.unsubscribe.complete();
  }

  public addNewRow(attribute: HierarchyTemplateAttribute): void {
    attribute.state = TableItemState.Added;
    attribute.refId = this.generateGuidService.newGuid();
    attribute.rowNumber = ++this.totalRecords;
    const attributes = [attribute, ...this.attributesValues$.value];

    this.dirtyFormStateService.setDirtyStatus(true);
    this.assignAttributes(attributes);
    this.tableConfigService.attributesConfig.enablePagination = false;
  }

  public updateEditedAttributeData(attribute: HierarchyTemplateAttribute): void {
    this.updateEditedAttributesData([attribute]);
    this.tableConfigService.attributesConfig.enablePagination = false;
  }

  public updateEditedAttributesData(attributes: HierarchyTemplateAttribute[]): void {
    attributes.forEach(attribute => this.modifyAttribute(attribute));

    this.updateAttributeList();
  }

  private modifyAttribute(attribute: HierarchyTemplateAttribute): void {

    if (attribute.state === TableItemState.Unchanged) {
      attribute.state = TableItemState.Modified;
    }

    attribute.refId = attribute.hierarchyTemplateAttributeId;
  }

  private updateAttributeList(): void {
    const attributes = this.attributesValues$.value.sort((a, b) => this.compareAttributes(a, b));

    this.attributesValues$.next(attributes);
    this.dirtyFormStateService.setDirtyStatus(true);
  }

  private compareAttributes(a: HierarchyTemplateAttribute, b: HierarchyTemplateAttribute) {

    if (a.state === TableItemState.Added && b.state !== TableItemState.Added) {
      return -1;
    }

    if (a.state !== TableItemState.Added && b.state === TableItemState.Added) {
      return 1;
    }

    if (a.state === TableItemState.Added && b.state === TableItemState.Added) {
      return a.rowNumber > b.rowNumber ? -1 : 1;
    }

    return a.rowNumber > b.rowNumber ? 1 : -1;
  }

  public onAttributesReorder(dragIndex: number, dropIndex: number): void {
    const reorderedAttributes: HierarchyTemplateAttribute[] = onRowsReorder<HierarchyTemplateAttribute>(
      this.attributesValues$.value,
      this.tableConfigService.rowNumberColumnName,
      dragIndex,
      dropIndex,
      (row: HierarchyTemplateAttribute) => (this.modifyAttribute(row))
    );

    this.assignAttributes(reorderedAttributes);
  }

  public createNewAttribute(): void {
    const attribute = new HierarchyTemplateAttribute();

    attribute.name = '';
    attribute.hierarchyTemplateId = this.templateId;
    attribute.groupName = '';
    attribute.tabName = '';
    attribute.lockedAttributes = false;

    this.attribute = attribute;
  }

  public clear(): void {
    this.templateId = '';
    this.attributesValues$.next([]);
    this.totalRecords = 0;
  }

  private assignAttributes(attributes: HierarchyTemplateAttribute[]): void {
    this.attributesValues$.next(attributes);
    this.selectedHierarchyTemplateGroup.attributes = attributes;
  }

  public setDirtyStatus(status: boolean): void {
    this.dirtyFormStateService.setDirtyStatus(status);
  }

  public showTableLoader(showLoader: boolean): void {
    if (this.tableConfigService && this.tableConfigService.attributesConfig) {
      this.tableConfigService.attributesConfig.loading = showLoader;
    }
  }

}
